/**
 * P2P Store - Zustand store for global P2P state management
 * Professional state management for useSelft Standalone P2P network
 * V70 terminology compliant
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { PeerConnectionManager } from '../services/PeerConnectionManager'
import { MessageService } from '../services/MessageService'
import { MediaService } from '../services/MediaService'
import { AliasRegistryService } from '../services/AliasRegistryService'
import { AvatarService } from '../services/AvatarService'
import { LocalStorageService } from '../services/LocalStorageService'
import { 
  SelfTA, 
  PeerConnection, 
  P2PMessage, 
  FileTransfer,
  P2PNetworkState,
  AliasRegistration 
} from '../types'
import { toastManager, TOAST_GROUPS } from '../utils/toastManager'
import { notificationSoundService } from '../services/NotificationSoundService'

export interface P2PState extends P2PNetworkState {
  // Service instances (not persisted)
  connectionManager: PeerConnectionManager | null
  messageService: MessageService | null
  mediaService: MediaService | null
  aliasRegistry: AliasRegistryService | null
  
  // UI State
  isInitializing: boolean
  isNetworkActive: boolean
  isNetworkStabilized: boolean
  connectionProgress: {
    phase: 'idle' | 'initializing' | 'stabilizing' | 'ready' | 'connecting' | 'discovering'
    message: string
    progress?: number
  }
  currentSelfta: SelfTA | null
  showConnectionDialog: boolean
  selectedPeerForChat: string | null
  
  // Statistics
  stats: {
    totalConnections: number
    totalMessages: number
    uptime: number
    lastActivity: Date | null
  }
}

export interface P2PActions {
  // Network Management
  initializeNetwork: (selfta: SelfTA) => Promise<boolean>
  shutdownNetwork: () => void
  
  // Connection Management
  connectToPeer: (peerId: string) => Promise<boolean>
  connectByAlias: (alias: string) => Promise<boolean>
  disconnectFromPeer: (peerId: string) => boolean
  disconnectAll: () => void
  
  // SelfTA Management
  updateSelfta: (updates: Partial<SelfTA>) => void
  updateAlias: (newAlias: string) => void
  
  // Connection State
  updatePeerConnection: (peerId: string, updates: Partial<PeerConnection>) => void
  removePeerConnection: (peerId: string) => void
  
  // Messaging
  addMessage: (message: P2PMessage) => void
  clearMessages: () => void
  markMessagesAsRead: (peerId?: string) => void
  
  // File Transfers
  addFileTransfer: (transfer: FileTransfer) => void
  updateFileTransfer: (id: string, updates: Partial<FileTransfer>) => void
  removeFileTransfer: (id: string) => void
  
  // UI State
  setInitializing: (isInitializing: boolean) => void
  setNetworkStabilized: (isStabilized: boolean) => void
  setConnectionProgress: (progress: P2PState['connectionProgress']) => void
  setShowConnectionDialog: (show: boolean) => void
  setSelectedPeerForChat: (peerId: string | null) => void
  
  // Network Error Handling
  setNetworkError: (error: string | undefined) => void
  clearNetworkError: () => void
  
  // Statistics
  updateStats: (updates: Partial<P2PState['stats']>) => void
  incrementConnectionCount: () => void
  incrementMessageCount: () => void
  updateLastActivity: () => void
  
  // Alias Registry
  getAliasRegistrations: () => AliasRegistration[]
  registerAlias: (alias: string, peerId: string) => void
  updateAliasRegistry: (registrations: AliasRegistration[]) => void
  
  // State restoration
  restoreNetworkState: () => void
  loadPersistedAvatar: (forceReload?: boolean) => void
  saveAvatarDataLocally: (avatarData: { alias: string; displayName: string; statusMessage: string; profileImage?: string | null }) => boolean
  autoReinitializeNetworkIfNeeded: () => Promise<boolean>

  // Alias-based connection methods
  getConnectionMethodInfo: () => { method: 'direct-alias' | 'legacy-id' | 'not-initialized'; peerId: string | null; alias: string | null; canDirectConnect: boolean }
  isUsingAliasAsPeerId: () => boolean
}

const initialState: Omit<P2PState, keyof P2PActions> = {
  // Network State
  localSelfta: null,
  connections: new Map(),
  messages: [],
  fileTransfers: [],
  isOnline: false,
  networkError: undefined,
  activeCall: undefined,

  // Service instances
  connectionManager: null,
  messageService: null,
  mediaService: null,
  aliasRegistry: null,
  
  // UI State
  isInitializing: false,
  isNetworkActive: false,
  isNetworkStabilized: false,
  connectionProgress: {
    phase: 'idle',
    message: 'Ready to connect'
  },
  currentSelfta: null,
  showConnectionDialog: false,
  selectedPeerForChat: null,
  
  // Statistics
  stats: {
    totalConnections: 0,
    totalMessages: 0,
    uptime: 0,
    lastActivity: null
  }
}

export const useP2PStore = create<P2PState & P2PActions>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // Network Management
        initializeNetwork: async (selfta: SelfTA) => {
          const state = get()
          
          if (state.isInitializing) {
            console.warn('Network initialization already in progress')
            return false
          }
          
          if (state.connectionManager) {
            console.warn('Network already initialized')
            return true
          }

          set({
            isInitializing: true,
            networkError: undefined,
            connectionProgress: {
              phase: 'initializing',
              message: 'Connecting to P2P network...',
              progress: 20
            }
          })

          try {
            // Create services
            const connectionManager = new PeerConnectionManager({}, {
              onConnection: (_event) => {
                const peerConnection = _event.data

                // MOBILE FIX: Enhanced connection handling with proper alias validation
                const displayName = peerConnection.displayName || peerConnection.alias || 'Connecting...'
                const hasValidAlias = Boolean(peerConnection.hasValidAlias ||
                  (peerConnection.alias && peerConnection.alias !== 'unknown' && !peerConnection.alias.startsWith('User_')))

                // Update connection with mobile-specific enhancements
                const enhancedConnection = {
                  ...peerConnection,
                  displayName,
                  hasValidAlias,
                  mobileOptimized: true,
                  connectionStabilized: !peerConnection.pendingHandshake
                }

                get().updatePeerConnection(peerConnection.id, enhancedConnection)
                get().incrementConnectionCount()
                get().updateLastActivity()

                // MOBILE FIX: Only show success toast after handshake completes
                if (!peerConnection.pendingHandshake && hasValidAlias) {
                  console.log(`✅ '${displayName}' has joined your network`)
                  toastManager.success(`${displayName} connected`, TOAST_GROUPS.CONNECTION, {
                    duration: 3000,
                    icon: '✅'
                  })
                } else {
                  console.log(`🔄 Connection established with ${peerConnection.id.slice(0, 8)}... - awaiting handshake`)
                }
              },
              
              onDisconnection: (event) => {
                const { peerId } = event.data
                const connection = get().connections.get(peerId)
                get().removePeerConnection(peerId)
                
                if (connection) {
                  console.log(`🔌 '${connection.displayName || connection.alias || 'Unknown User'}' has left the conversation`)
                  toastManager.info(`${connection.displayName || connection.alias || 'Unknown User'} disconnected`, TOAST_GROUPS.CONNECTION, {
                    icon: '🔌',
                    duration: 3000,
                    style: {
                      background: '#f9fafb',
                      color: '#6b7280'
                    }
                  })
                }
                
                get().updateLastActivity()
              },
              
              onData: (_event) => {
                get().updateLastActivity()
                // Data handling will be delegated to appropriate services
              },
              
              onError: (event) => {
                const { error } = event.data
                console.error('P2P Connection Error:', error)
                get().setNetworkError(error.message)
                console.error('P2P Connection Error Details:', error)
                toastManager.error('Connection error', TOAST_GROUPS.CONNECTION, {
                  duration: 4000,
                  icon: '⚠️'
                })
              }
            })

            // Create alias registry (don't register local alias yet - wait for peerId)
            const aliasRegistry = new AliasRegistryService()

            // Create service placeholders (will be updated with actual peerId)
            let messageService: MessageService | null = null
            let mediaService: MediaService | null = null

            // Initialize connection manager with callback to handle peerId assignment
            const success = await connectionManager.initializeP2P(selfta, (assignedPeerId: string) => {
              // Set stabilizing progress
              set({
                connectionProgress: {
                  phase: 'stabilizing',
                  message: 'Stabilizing network connection...',
                  progress: 60
                }
              })

              // This callback is called when PeerJS assigns the actual peerId
              const updatedSelfta = { ...selfta, id: assignedPeerId }

              // Check if we're using alias as peer ID (direct alias connections)
              const isUsingAliasAsPeerId = assignedPeerId === selfta.alias
              console.log(`🔗 Connection method: ${isUsingAliasAsPeerId ? 'direct-alias' : 'legacy-id'}`)

              // Now register the alias with the actual peerId
              aliasRegistry.registerLocalAlias(updatedSelfta.alias, assignedPeerId)

              // Create message service with actual peerId
              messageService = new MessageService(assignedPeerId, updatedSelfta.alias)

              // Get media service from connection manager (already initialized and updated with PeerJS instance)
              mediaService = connectionManager.getMediaService()

              // CRITICAL FIX: Update PeerConnectionManager to use the same MessageService instance
              connectionManager.updateMessageService(messageService)

              // Setup message event handlers
              messageService.on('message-received', (event) => {
                const message = event.data as P2PMessage
                get().addMessage(message)
                get().incrementMessageCount()

                // Play notification sound for incoming messages
                notificationSoundService.playNotificationSound().catch(console.warn)

                toastManager.info(`New message from '${message.senderAlias}'`, TOAST_GROUPS.GENERAL, {
                  icon: '💬',
                  duration: 3000,
                  style: {
                    background: '#eff6ff',
                    color: '#1e40af'
                  }
                })
              })

              messageService.on('message-sent', (event) => {
                const message = event.data as P2PMessage
                get().addMessage(message)
                get().incrementMessageCount()
              })

              messageService.on('typing-started', (event) => {
                const indicator = event.data as { peerId: string }
                get().updatePeerConnection(indicator.peerId, { isTyping: true })
              })

              messageService.on('typing-stopped', (event) => {
                const indicator = event.data as { peerId: string }
                get().updatePeerConnection(indicator.peerId, { isTyping: false })
              })

              // Save successful session to localStorage
              LocalStorageService.saveLastSession(assignedPeerId, updatedSelfta.alias)

              // Update state with actual peerId - initially not stabilized
              set({
                connectionManager,
                messageService,
                mediaService,
                aliasRegistry,
                localSelfta: updatedSelfta,
                currentSelfta: updatedSelfta,
                isOnline: true,
                isNetworkActive: true,
                isInitializing: false,
                isNetworkStabilized: false,
                connectionProgress: {
                  phase: 'stabilizing',
                  message: 'Waiting for network stabilization...',
                  progress: 80
                },
                stats: {
                  ...get().stats,
                  lastActivity: new Date()
                }
              })

              // Set up stabilization completion timer
              setTimeout(() => {
                set({
                  isNetworkStabilized: true,
                  connectionProgress: {
                    phase: 'ready',
                    message: 'Network ready for connections',
                    progress: 100
                  }
                })
                console.log(`🌐 P2P Network stabilization completed`)
              }, 3000) // Match the stabilization time in PeerConnectionManager

              const connectionMethod = assignedPeerId === updatedSelfta.alias ? 'direct-alias' : 'legacy-id'
              console.log(`✅ P2P Network fully initialized: ${updatedSelfta.alias} -> ${assignedPeerId.slice(0, 8)}... (${connectionMethod})`)
            })
            
            if (!success) {
              throw new Error('Failed to initialize P2P connection')
            }

            console.log('🌐 P2P Network initialization completed')
            // Network ready notification is handled by PeerConnectionManager
            // No duplicate toast notification here
            return true

          } catch (error) {
            console.error('Failed to initialize P2P network:', error)
            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            set({ 
              isInitializing: false,
              networkError: errorMessage
            })
            console.error('Network initialization failed with details:', errorMessage)
            toastManager.error('Network initialization failed', TOAST_GROUPS.NETWORK, {
              duration: 5000,
              icon: '❌'
            })
            return false
          }
        },

        shutdownNetwork: () => {
          const state = get()

          // Destroy services
          if (state.connectionManager) {
            state.connectionManager.destroy()
          }

          if (state.messageService) {
            state.messageService.destroy()
          }

          if (state.mediaService) {
            state.mediaService.destroy()
          }

          // Reset state
          set({
            ...initialState,
            stats: state.stats // Preserve statistics
          })
          
          console.log('🔌 P2P Network shutdown')
          toastManager.info('P2P Network disconnected - you are now offline', TOAST_GROUPS.CONNECTION, {
            icon: '🔌',
            duration: 4000,
            style: {
              background: '#fef2f2',
              color: '#991b1b'
            }
          })
        },

        // Connection Management
        connectToPeer: async (peerId: string) => {
          const { connectionManager } = get()
          if (!connectionManager) {
            console.error('Network not initialized. Please join the P2P network first.')
            toastManager.error('Network not ready', TOAST_GROUPS.NETWORK, {
              duration: 4000,
              icon: '⚠️'
            })
            return false
          }
          
          try {
            return await connectionManager.connectToPeer(peerId)
          } catch (error) {
            console.error('Error connecting to peer:', error)
            console.error('Error connecting to peer details:', error)
            toastManager.error('Failed to connect to peer', TOAST_GROUPS.CONNECTION, {
              duration: 4000,
              icon: '❌'
            })
            return false
          }
        },

        connectByAlias: async (alias: string) => {
          const { connectionManager } = get()
          if (!connectionManager) {
            console.error('Network not initialized. Please join the P2P network first.')
            toastManager.error('Network not ready', TOAST_GROUPS.NETWORK, {
              duration: 4000,
              icon: '⚠️'
            })
            return false
          }

          // Check connection method capability
          const connectionMethodInfo = connectionManager.getConnectionMethodInfo()
          const connectingViaMethod = connectionMethodInfo.method === 'direct-alias' ? 'direct alias' : 'network discovery'

          // Set connection progress to discovering
          set({
            connectionProgress: {
              phase: 'discovering',
              message: `Connecting to '${alias}' via ${connectingViaMethod}...`,
              progress: 0
            }
          })

          try {
            const result = await connectionManager.connectByAlias(alias)

            if (result) {
              // Connection successful
              set({
                connectionProgress: {
                  phase: 'ready',
                  message: 'Network ready for connections',
                  progress: 100
                }
              })
            } else {
              // Connection failed
              set({
                connectionProgress: {
                  phase: 'ready',
                  message: 'Network ready for connections',
                  progress: 100
                }
              })
            }

            return result
          } catch (error) {
            console.error('Error connecting by alias:', error)
            console.error(`Error connecting by alias '${alias}':`, error)
            toastManager.error('Connection failed', TOAST_GROUPS.CONNECTION, {
              duration: 4000,
              icon: '❌'
            })

            // Reset to ready state on error
            set({
              connectionProgress: {
                phase: 'ready',
                message: 'Network ready for connections',
                progress: 100
              }
            })

            return false
          }
        },

        disconnectFromPeer: (peerId: string) => {
          const { connectionManager } = get()
          if (!connectionManager) {
            return false
          }
          
          return connectionManager.disconnectFromPeer(peerId)
        },

        disconnectAll: () => {
          const { connectionManager } = get()
          if (connectionManager) {
            connectionManager.disconnectAll()
          }
        },

        // SelfTA Management
        updateSelfta: (updates: Partial<SelfTA>) => {
          const currentSelfta = get().localSelfta
          if (!currentSelfta) return
          
          // Create auto-backup before updating (for significant changes)
          const significantChanges = ['alias', 'displayName', 'profileImage']
          const hasSignificantChange = significantChanges.some(key => key in updates)
          
          if (hasSignificantChange) {
            AvatarService.backupAvatar(currentSelfta, 'auto')
              .then(() => console.log('✅ Auto-backup created before avatar update'))
              .catch(error => console.warn('⚠️ Auto-backup failed:', error))
          }
          
          const updatedSelfta: SelfTA = {
            ...currentSelfta,
            ...updates,
            lastUpdated: new Date()
          }
          
          set({ localSelfta: updatedSelfta, currentSelfta: updatedSelfta })
          
          // Save updated avatar to localStorage for persistence
          const saveResult = LocalStorageService.saveAvatarData({
            alias: updatedSelfta.alias,
            displayName: updatedSelfta.displayName,
            statusMessage: updatedSelfta.statusMessage,
            profileImage: updatedSelfta.profileImage || null
          })
          
          if (saveResult) {
            console.log('✅ Avatar data saved to localStorage with image:', !!updatedSelfta.profileImage)
          } else {
            console.warn('⚠️ Failed to save avatar data to localStorage')
          }
          
          // Update message service if alias changed
          if (updates.alias && get().messageService) {
            get().messageService?.updateLocalPeer(updatedSelfta.id, updates.alias)
          }
        },

        updateAlias: (newAlias: string) => {
          get().updateSelfta({ alias: newAlias })
          
          // Broadcast alias update to network
          const { connectionManager, aliasRegistry } = get()
          if (connectionManager && aliasRegistry) {
            // This would trigger a network broadcast in the real implementation
            console.log(`Broadcasting alias update: ${newAlias}`)
            console.log(`Your alias has been updated to '${newAlias}'`)
            toastManager.success(`Alias updated`, TOAST_GROUPS.AVATAR, {
              duration: 3000,
              icon: '✅'
            })
          }
        },

        // Connection State Management
        updatePeerConnection: (peerId: string, updates: Partial<PeerConnection>) => {
          set(state => {
            const newConnections = new Map(state.connections)
            const existingConnection = newConnections.get(peerId)

            if (existingConnection) {
              // MOBILE FIX: Merge updates while preserving mobile-specific flags
              const updatedConnection = {
                ...existingConnection,
                ...updates,
                lastSeen: new Date(),
                // Preserve or update mobile-specific properties
                hasValidAlias: Boolean(updates.hasValidAlias ?? existingConnection.hasValidAlias),
                pendingHandshake: Boolean(updates.pendingHandshake ?? existingConnection.pendingHandshake),
                mobileOptimized: existingConnection.mobileOptimized ?? true
              }

              // MOBILE FIX: Update display name if alias was updated
              if (updates.alias && updates.alias !== existingConnection.alias) {
                updatedConnection.displayName = updates.displayName || updates.alias
                updatedConnection.selfta.displayName = updatedConnection.displayName
                updatedConnection.selfta.alias = updates.alias
              }

              newConnections.set(peerId, updatedConnection)
            } else {
              // Create new connection if it doesn't exist
              // MOBILE FIX: Better fallback system with mobile considerations
              const fallbackAlias = updates.alias || 'unknown'
              const fallbackDisplayName = updates.displayName || updates.alias || 'Connecting...'
              const isValidAlias = fallbackAlias !== 'unknown' && !fallbackAlias.startsWith('User_')

              const newConnection: PeerConnection = {
                id: peerId,
                peerId,
                alias: fallbackAlias,
                displayName: fallbackDisplayName,
                selfta: {
                  id: peerId,
                  alias: fallbackAlias,
                  displayName: fallbackDisplayName,
                  statusMessage: 'Connected',
                  createdAt: new Date(),
                  lastUpdated: new Date()
                },
                connectionState: 'connected',
                connectionQuality: 'good',
                isConnected: true,
                lastSeen: new Date(),
                // MOBILE FIX: Add mobile-specific properties
                hasValidAlias: isValidAlias,
                pendingHandshake: Boolean(updates.pendingHandshake ?? true),
                mobileOptimized: true,
                ...updates
              }
              newConnections.set(peerId, newConnection)
            }

            return { connections: newConnections }
          })
        },

        removePeerConnection: (peerId: string) => {
          set(state => {
            const newConnections = new Map(state.connections)
            const removedConnection = newConnections.get(peerId)

            // MOBILE FIX: Log removal with proper alias information
            if (removedConnection) {
              const displayName = removedConnection.displayName || removedConnection.alias || 'Unknown User'
              console.log(`🔌 Removing connection: ${displayName} (${peerId.slice(0, 8)}...)`)
            }

            newConnections.delete(peerId)
            return { connections: newConnections }
          })
        },

        // Messaging
        addMessage: (message: P2PMessage) => {
          set(state => ({
            messages: [...state.messages, message].sort((a, b) => 
              a.timestamp.getTime() - b.timestamp.getTime()
            ).slice(-1000) // Keep only last 1000 messages
          }))
        },

        clearMessages: () => {
          set({ messages: [] })
          toastManager.info('Message history has been cleared', TOAST_GROUPS.GENERAL, {
            icon: '🧹',
            duration: 3000,
            style: {
              background: '#f9fafb',
              color: '#6b7280'
            }
          })
        },

        markMessagesAsRead: (peerId?: string) => {
          // Implementation for marking messages as read
          console.log(`Marking messages as read${peerId ? ` from ${peerId}` : ''}`)
        },

        // File Transfers
        addFileTransfer: (transfer: FileTransfer) => {
          set(state => ({
            fileTransfers: [...state.fileTransfers, transfer]
          }))
        },

        updateFileTransfer: (id: string, updates: Partial<FileTransfer>) => {
          set(state => ({
            fileTransfers: state.fileTransfers.map(transfer =>
              transfer.id === id ? { ...transfer, ...updates } : transfer
            )
          }))
        },

        removeFileTransfer: (id: string) => {
          set(state => ({
            fileTransfers: state.fileTransfers.filter(transfer => transfer.id !== id)
          }))
        },

        // UI State Management
        setInitializing: (isInitializing: boolean) => {
          set({ isInitializing })
        },

        setNetworkStabilized: (isStabilized: boolean) => {
          set({ isNetworkStabilized: isStabilized })
        },

        setConnectionProgress: (progress: P2PState['connectionProgress']) => {
          set({ connectionProgress: progress })
        },

        setShowConnectionDialog: (show: boolean) => {
          set({ showConnectionDialog: show })
        },

        setSelectedPeerForChat: (peerId: string | null) => {
          set({ selectedPeerForChat: peerId })
        },

        // Network Error Handling
        setNetworkError: (error: string | undefined) => {
          set({ networkError: error })
        },

        clearNetworkError: () => {
          set({ networkError: undefined })
        },

        // Statistics
        updateStats: (updates: Partial<P2PState['stats']>) => {
          set(state => ({
            stats: { ...state.stats, ...updates }
          }))
        },

        incrementConnectionCount: () => {
          set(state => ({
            stats: {
              ...state.stats,
              totalConnections: state.stats.totalConnections + 1
            }
          }))
        },

        incrementMessageCount: () => {
          set(state => ({
            stats: {
              ...state.stats,
              totalMessages: state.stats.totalMessages + 1
            }
          }))
        },

        updateLastActivity: () => {
          set(state => ({
            stats: {
              ...state.stats,
              lastActivity: new Date()
            }
          }))
        },

        // Alias Registry
        getAliasRegistrations: () => {
          const { aliasRegistry } = get()
          return aliasRegistry?.getAllAliases() || []
        },

        registerAlias: (alias: string, peerId: string) => {
          const { aliasRegistry } = get()
          aliasRegistry?.registerPeerAlias(alias, peerId)
        },

        updateAliasRegistry: (registrations: AliasRegistration[]) => {
          // This would update the alias registry with external data
          console.log('Updating alias registry with', registrations.length, 'registrations')
        },

        // State restoration after persistence
        restoreNetworkState: () => {
          const state = get()
          if (state.localSelfta && !state.connectionManager) {
            console.log('🔄 Detected persisted avatar but no active network. Network needs to be reinitialized.')
            set({ isOnline: false })
          }
        },

        // Load persisted avatar from localStorage - force reload option
        loadPersistedAvatar: (forceReload = false) => {
          const state = get()
          
          console.log('🔍 Loading avatar from localStorage...', { forceReload, hasExistingSelfta: !!state.localSelfta })
          const storedAvatar = LocalStorageService.loadAvatarData()
          
          if (storedAvatar) {
            // Check if we need to update (force reload or data has changed)
            const needsUpdate = forceReload || 
              !state.localSelfta || 
              state.localSelfta.alias !== storedAvatar.alias ||
              state.localSelfta.displayName !== storedAvatar.displayName ||
              state.localSelfta.statusMessage !== storedAvatar.statusMessage ||
              state.localSelfta.profileImage !== storedAvatar.profileImage
            
            if (needsUpdate) {
              // Create a SelfTA with stored data
              const updatedSelfta: SelfTA = {
                id: state.localSelfta?.id || '', // Preserve existing ID if available
                alias: storedAvatar.alias,
                displayName: storedAvatar.displayName,
                statusMessage: storedAvatar.statusMessage,
                profileImage: storedAvatar.profileImage || null,
                createdAt: state.localSelfta?.createdAt ?
                  (typeof state.localSelfta.createdAt === 'string' ?
                    new Date(state.localSelfta.createdAt) :
                    state.localSelfta.createdAt) :
                  new Date(),
                lastUpdated: new Date()
              }
              
              set({ 
                localSelfta: updatedSelfta,
                currentSelfta: updatedSelfta 
              })
              
              console.log('✅ Avatar loaded/updated from localStorage:', {
                alias: storedAvatar.alias,
                hasImage: !!storedAvatar.profileImage,
                imageSize: storedAvatar.profileImage?.length || 0
              })
              return
            } else {
              console.log('✅ Avatar already up-to-date in store:', state.localSelfta?.alias || 'unknown')
              return
            }
          }
          
          console.log('ℹ️ No avatar data found in localStorage')
        },

        // Save avatar data locally without requiring network connection
        saveAvatarDataLocally: (avatarData: { alias: string; displayName: string; statusMessage: string; profileImage?: string | null }) => {
          const state = get()

          // Save to localStorage first
          const localSaveResult = LocalStorageService.saveAvatarData({
            alias: avatarData.alias,
            displayName: avatarData.displayName,
            statusMessage: avatarData.statusMessage,
            profileImage: avatarData.profileImage || null
          })

          if (localSaveResult) {
            // Create or update Zustand store state
            const updatedSelfta: SelfTA = {
              id: state.localSelfta?.id || '', // Preserve existing ID or empty for offline
              alias: avatarData.alias,
              displayName: avatarData.displayName,
              statusMessage: avatarData.statusMessage,
              profileImage: avatarData.profileImage || null,
              createdAt: state.localSelfta?.createdAt ?
                (typeof state.localSelfta.createdAt === 'string' ?
                  new Date(state.localSelfta.createdAt) :
                  state.localSelfta.createdAt) :
                new Date(),
              lastUpdated: new Date()
            }

            set({
              localSelfta: updatedSelfta,
              currentSelfta: updatedSelfta
            })

            console.log('✅ Avatar data saved locally to both localStorage and store:', {
              alias: avatarData.alias,
              hasImage: !!avatarData.profileImage,
              imageSize: avatarData.profileImage?.length || 0
            })

            return true
          }

          console.error('❌ Failed to save avatar data to localStorage')
          return false
        },

        // Get connection method information from the connection manager
        getConnectionMethodInfo: () => {
          const { connectionManager } = get()
          if (!connectionManager) {
            return {
              method: 'not-initialized' as const,
              peerId: null,
              alias: null,
              canDirectConnect: false
            }
          }
          return connectionManager.getConnectionMethodInfo()
        },

        // Check if using alias as peer ID
        isUsingAliasAsPeerId: () => {
          const { connectionManager } = get()
          return connectionManager?.isUsingAliasAsPeerId() ?? false
        },

        // Auto-reinitialize network if needed (for persistence across navigation)
        autoReinitializeNetworkIfNeeded: async () => {
          const state = get()

          // Only reinitialize if we have a localSelfta but no active connection manager
          if (state.localSelfta && !state.connectionManager && !state.isInitializing) {
            console.log('🔄 Auto-reinitializing network for persistent state...')

            try {
              const success = await get().initializeNetwork(state.localSelfta)
              if (success) {
                console.log('✅ Network auto-reinitialized successfully')
                return true
              } else {
                console.warn('⚠️ Network auto-reinitialization failed')
                return false
              }
            } catch (error) {
              console.error('❌ Error during network auto-reinitialization:', error)
              return false
            }
          }

          // Network is already active or no need to reinitialize
          return !!state.connectionManager
        }
      }),
      {
        name: 'useselft-p2p-store',
        partialize: (state) => ({
          // Only persist essential data, not service instances
          localSelfta: state.localSelfta,
          messages: state.messages.slice(-100), // Only keep last 100 messages
          stats: state.stats,
          // CRITICAL FIX: Persist network state to maintain connection across navigation
          isOnline: state.isOnline,
          isNetworkStabilized: state.isNetworkStabilized,
          // Don't persist: connections (they need to be re-established)
          // Don't persist: service instances
          // Don't persist: other UI state
        }),
        version: 1,
        migrate: (persistedState: any, version: number) => {
          // Handle state migrations between versions
          if (version === 0) {
            // Migration logic from version 0 to 1
            return {
              ...persistedState,
              stats: initialState.stats
            }
          }
          return persistedState
        },
        onRehydrateStorage: () => (state) => {
          // Called after state is rehydrated from storage
          if (state) {
            console.log('🔄 Store rehydrating...', { hasLocalSelfta: !!state.localSelfta })

            // CRITICAL FIX: Convert date strings back to Date objects after JSON deserialization
            if (state.localSelfta) {
              // Convert createdAt and lastUpdated from strings to Date objects
              if (typeof state.localSelfta.createdAt === 'string') {
                state.localSelfta.createdAt = new Date(state.localSelfta.createdAt)
                console.log('🔄 Converted createdAt from string to Date object')
              }
              if (typeof state.localSelfta.lastUpdated === 'string') {
                state.localSelfta.lastUpdated = new Date(state.localSelfta.lastUpdated)
                console.log('🔄 Converted lastUpdated from string to Date object')
              }
            }

            // Convert stats.lastActivity from string to Date object if needed
            if (state.stats?.lastActivity && typeof state.stats.lastActivity === 'string') {
              state.stats.lastActivity = new Date(state.stats.lastActivity)
              console.log('🔄 Converted stats.lastActivity from string to Date object')
            }

            // Convert message timestamps from strings to Date objects
            if (state.messages && Array.isArray(state.messages)) {
              state.messages.forEach(message => {
                if (typeof message.timestamp === 'string') {
                  message.timestamp = new Date(message.timestamp)
                }
              })
              console.log('🔄 Converted message timestamps from strings to Date objects')
            }

            // Convert connection date fields from strings to Date objects
            if (state.connections && state.connections instanceof Map) {
              state.connections.forEach(connection => {
                if (connection.connectedAt && typeof connection.connectedAt === 'string') {
                  connection.connectedAt = new Date(connection.connectedAt)
                }
                if (connection.lastSeen && typeof connection.lastSeen === 'string') {
                  connection.lastSeen = new Date(connection.lastSeen)
                }
                // Also fix dates in the nested selfta object
                if (connection.selfta) {
                  if (typeof connection.selfta.createdAt === 'string') {
                    connection.selfta.createdAt = new Date(connection.selfta.createdAt)
                  }
                  if (typeof connection.selfta.lastUpdated === 'string') {
                    connection.selfta.lastUpdated = new Date(connection.selfta.lastUpdated)
                  }
                }
              })
              console.log('🔄 Converted connection date fields from strings to Date objects')
            }

            // CRITICAL FIX: Handle network state restoration properly
            if (state.localSelfta) {
              if (!state.connectionManager && state.isOnline) {
                // We have a SelfTA and should be online, but no connection manager
                // This means we need to reinitialize the network
                console.log('🔄 Store rehydrated: Avatar found with online state but no connection manager - network needs reinitialization')
                state.isOnline = false
                state.isNetworkStabilized = false
              } else if (!state.connectionManager && !state.isOnline) {
                console.log('🔄 Store rehydrated: Avatar found but network not connected (as expected)')
              } else if (state.connectionManager && state.isOnline) {
                console.log('🔄 Store rehydrated: Avatar found with active network connection')
              }
            }

            // CRITICAL FIX: Always prioritize localStorage data over Zustand persisted data
            // This ensures the most recent avatar data is always loaded and fixes mobile persistence
            try {
              const storedAvatar = LocalStorageService.loadAvatarData()
              if (storedAvatar) {
                console.log('🔄 Loading fresh avatar data from localStorage during rehydration')

                // MOBILE FIX: Validate image data before using it
                let validatedProfileImage = storedAvatar.profileImage
                if (storedAvatar.profileImage) {
                  // Check if image data is valid base64
                  const isValidBase64 = /^data:image\/(png|jpe?g|gif|svg\+xml|webp);base64,/.test(storedAvatar.profileImage)
                  if (!isValidBase64) {
                    console.warn('⚠️ Invalid image data detected during rehydration, clearing image')
                    validatedProfileImage = null
                  } else {
                    console.log('✅ Valid image data found:', {
                      size: storedAvatar.profileImage.length,
                      type: storedAvatar.profileImage.match(/^data:image\/(\w+);/)?.[1] || 'unknown'
                    })
                  }
                }

                const updatedSelfta: SelfTA = {
                  id: state.localSelfta?.id || '',
                  alias: storedAvatar.alias,
                  displayName: storedAvatar.displayName,
                  statusMessage: storedAvatar.statusMessage,
                  profileImage: validatedProfileImage || null, // Use validated image data
                  createdAt: state.localSelfta?.createdAt || new Date(),
                  lastUpdated: new Date()
                }

                state.localSelfta = updatedSelfta
                state.currentSelfta = updatedSelfta
                console.log('✅ Avatar data synchronized during rehydration:', {
                  alias: storedAvatar.alias,
                  hasImage: !!validatedProfileImage,
                  imageSize: validatedProfileImage?.length || 0,
                  createdAtType: typeof updatedSelfta.createdAt,
                  lastUpdatedType: typeof updatedSelfta.lastUpdated
                })
              } else if (!state.localSelfta) {
                console.log('ℹ️ No avatar data found in localStorage or Zustand during rehydration')
              }
            } catch (error) {
              console.error('❌ Error during avatar rehydration:', error)
              // Don't crash the app, just log the error
            }
          }
        }
      }
    ),
    {
      name: 'useSelft P2P Store',
      enabled: process.env.NODE_ENV === 'development'
    }
  )
)

// Computed selectors for common use cases
export const useP2PSelectors = {
  // Get connected peer count
  useConnectedPeerCount: () => useP2PStore(state => state.connections.size),
  
  // Get messages with a specific peer
  useMessagesWithPeer: (peerId: string) => useP2PStore(
    state => state.messages.filter(msg => 
      msg.senderId === peerId || 
      msg.recipients.includes(peerId) ||
      (msg.senderId === state.localSelfta?.id && msg.recipients.includes(peerId))
    )
  ),
  
  // Get typing peers
  useTypingPeers: () => useP2PStore(
    state => Array.from(state.connections.values())
      .filter(peer => peer.isTyping)
      .map(peer => ({ id: peer.id, alias: peer.alias }))
  ),
  
  // Get connection status summary
  useConnectionStatus: () => useP2PStore(state => ({
    isOnline: state.isOnline,
    isInitializing: state.isInitializing,
    isNetworkStabilized: state.isNetworkStabilized,
    connectionProgress: state.connectionProgress,
    connectionCount: state.connections.size,
    hasError: !!state.networkError,
    localPeerId: state.localSelfta?.id || null
  })),
  
  // Get network statistics
  useNetworkStats: () => useP2PStore(state => state.stats),

  // Get alias-based connection info
  useConnectionMethodInfo: () => useP2PStore(state => {
    const methodInfo = state.connectionManager?.getConnectionMethodInfo() ?? {
      method: 'not-initialized' as const,
      peerId: null,
      alias: null,
      canDirectConnect: false
    }
    return {
      ...methodInfo,
      isUsingAlias: methodInfo.method === 'direct-alias',
      connectionReady: methodInfo.canDirectConnect
    }
  }),

  // Check if using alias as peer ID
  useIsUsingAliasAsPeerId: () => useP2PStore(state =>
    state.connectionManager?.isUsingAliasAsPeerId() ?? false
  )
}